import { Component, OnInit } from '@angular/core';
import { ActionSheetController, AlertController, NavController, Platform } from '@ionic/angular';
import { AppModeService } from 'src/app/angular-wallet-base/services/app-mode.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../../angular-wallet-base/store/appState';
import { getBaseCurrency } from '../../../angular-wallet-base/store/userPreferences';
import { take } from 'rxjs/operators';
import { SysTxAssetAllocationSend } from '../../../global';
import { NetworkService } from '../../../angular-wallet-base/services/network.service';
import { TranslateService } from '@ngx-translate/core';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { handleCopy, isTxASwap, openLinkExternal, TxSummary, weiToNum } from 'src/app/angular-wallet-base/utils';
import { ClipboardService } from 'ngx-clipboard';
import { Clipboard } from '@ionic-native/clipboard/ngx';
import { Logger } from 'src/app/angular-wallet-base/services/logger.service';
import { GraphqlService, TraderJoeSwapTransactionData } from 'src/app/angular-wallet-base/services/graphql.service';
import { SmallToCryptoPipe } from 'src/app/angular-wallet-base/pipes/smalltocrypto/smalltocrypto.pipe';
import { inspect } from 'util';
export class TransactionDetailData {
  constructor(
    public id: string,
    public sendToken: string,
    public sendAmount: number,
    public receiveToken: string,
    public receiveAmount: number,
    public amountUSD: number
  ) { }

  public static buildFromTraderJoeSwap(traderJoeSwapTransactionData: TraderJoeSwapTransactionData): TransactionDetailData {
    let sendToken, sendAmount, receiveToken, receiveAmount;
    if (traderJoeSwapTransactionData.swaps[0].amount0In != '0') {
      sendToken = traderJoeSwapTransactionData.swaps[0].pair.token0.symbol;
      sendAmount = Number(traderJoeSwapTransactionData.swaps[0].amount0In);
    } else {
      sendToken = traderJoeSwapTransactionData.swaps[0].pair.token1.symbol;
      sendAmount = Number(traderJoeSwapTransactionData.swaps[0].amount1In);
    }

    const noSwaps = traderJoeSwapTransactionData.swaps.length;
    if (traderJoeSwapTransactionData.swaps[noSwaps - 1].amount0Out != '0') {
      receiveToken = traderJoeSwapTransactionData.swaps[noSwaps - 1].pair.token0.symbol;
      receiveAmount = Number(traderJoeSwapTransactionData.swaps[noSwaps - 1].amount0Out);
    } else {
      receiveToken = traderJoeSwapTransactionData.swaps[noSwaps - 1].pair.token1.symbol;
      receiveAmount = Number(traderJoeSwapTransactionData.swaps[noSwaps - 1].amount1Out);
    }
    // all swaps in the same transaction have the same amount USD and USDC decimal is 6
    let amountUSD = Number(traderJoeSwapTransactionData.swaps[0].amountUSD);
    return new TransactionDetailData(traderJoeSwapTransactionData.id, sendToken, sendAmount, receiveToken, receiveAmount, amountUSD);
  }
}
@Component({
  selector: 'app-tran-details-page',
  templateUrl: './transaction-details.component.html',
  styleUrls: ['./transaction-details.component.scss'],
})
export class TransactionDetailsComponent implements OnInit {
  public isDesktopMode;
  public tokenInfo: any;
  public transaction: TxSummary;
  public baseCurrency: string;
  public baseCurrencyAmount;
  public fee: number;
  public auxFee: number;
  public auxFeeAddress: string;
  public transactionDetail: TransactionDetailData = new TransactionDetailData("0x00", "AGX", 0, "USDC", 0, 0);
  public time: any;

  constructor(
    private navCtrl: NavController,
    private appMode: AppModeService,
    private route: ActivatedRoute,
    private router: Router,
    private networkService: NetworkService,
    protected store: Store<AppState>,
    public actionSheetController: ActionSheetController,
    private translate: TranslateService,
    private inAppBrowser: InAppBrowser,
    private platform: Platform,
    private clipboard: Clipboard,
    public alertController: AlertController,
    private clipboardService: ClipboardService,
    private graphqlService: GraphqlService,
    private smallToCryptoPipe: SmallToCryptoPipe
  ) {
    this.appMode.isDesktopMode.subscribe((mode) => (this.isDesktopMode = mode));

    this.route.queryParams.subscribe((params) => {
      if (this.router.getCurrentNavigation().extras.state) {
        this.transaction = this.router.getCurrentNavigation().extras.state.txnDetails;
        this.tokenInfo = this.router.getCurrentNavigation().extras.state.tokenInfo;
      }
    });
  }

  async ngOnInit() {
    this.fee = this.transaction.fee;
    this.time = this.transaction.tokenInfo.baseChainSymbol === 'BTC' ? this.transaction.time : Number(this.transaction.time) * 1000;
    this.baseCurrency = await this.store.select(getBaseCurrency).pipe(take(1)).toPromise();
    this.transactionDetail = await this.buildTransactionDetail(this.transaction);

    if (this.transaction.auxFee && this.transaction.auxFeeAddress) {
      this.auxFee = this.transaction.auxFee;
      this.auxFeeAddress = this.transaction.auxFeeAddress;
    }
    const assetAllocationSend = this.transaction.systx as SysTxAssetAllocationSend;

    if (
      !this.isBaseCoinTransaction() &&
      this.transaction.tokenInfo.auxFeeAddress &&
      assetAllocationSend &&
      assetAllocationSend.allocations[1]
    ) {
      const network = await this.networkService.getActiveSysNetwork();
      this.auxFeeAddress = this.getFeeAddressbyNetwork(network);
    }
  }

  private async buildTransactionDetail(transaction: any): Promise<TransactionDetailData> {
    if (transaction.tokenInfo.baseChainSymbol.toUpperCase() === 'AVAX') {
      return this.buildAvaxTransactionDetail(transaction);
    } else {
      // eth
      return this.buildNativeEVMTransactionDetail(transaction);
    }
  }

  private async buildAvaxTransactionDetail(transaction: any): Promise<TransactionDetailData> {
    if (isTxASwap(this.transaction.toAddress)) {
      const transactionData = await this.graphqlService.getTraderJoeTransactionDetail(transaction.txid);
      return TransactionDetailData.buildFromTraderJoeSwap(transactionData);
    } else {
      return this.buildNativeEVMTransactionDetail(transaction);
    }
  }

  private async buildNativeEVMTransactionDetail(transaction: any): Promise<TransactionDetailData> {
    let transactionAmount = this.smallToCryptoPipe.transform(transaction.amount, {
      type: this.tokenInfo.symbol,
      precision: this.tokenInfo.precision,
    });
    let baseCurrencyAmount = transactionAmount * (this.tokenInfo.retailFiatValue || this.tokenInfo.fiatValue);
    if (transaction.type === 'receive') {
      return new TransactionDetailData(transaction.txid, 'USDC', 0, this.tokenInfo.symbol, transactionAmount, baseCurrencyAmount);
    } else {
      return new TransactionDetailData(transaction.txid, this.tokenInfo.symbol, transactionAmount, 'USDC', 0, baseCurrencyAmount);
    }
  }

  isBaseCoinTransaction() {
    return this.tokenInfo.symbol === this.tokenInfo.baseChainSymbol;
  }

  isSysTransaction() {
    return this.tokenInfo.baseChainSymbol === 'SYS';
  }

  getFeeAddressbyNetwork(network) {
    return this.transaction.tokenInfo.auxFeeAddress.find((address) => {
      const addressFirstLetters = address.slice(0, 4);
      return network.network === 'testnet' ? addressFirstLetters === 'tsys' : addressFirstLetters === 'sys1';
    });
  }

  goBack() {
    this.navCtrl.back();
  }
  async presentActionSheet(event: string, isAddress: boolean) {
    
      const activeNetwork = await this.networkService.getActiveNetwork(this.transaction?.tokenInfo?.baseChainSymbol)
      let explorerUrl;
    
      switch (this.transaction.tokenInfo.baseChainSymbol) {
        case 'SYS':
        case 'BTC':
          explorerUrl = activeNetwork.EXPLORER_URL;
          break;
        case 'ETH':
        case 'AVAX':
          explorerUrl = activeNetwork.API_EXPLORER;
          break;
        // TODO: ADD CASE OF SOLANA IN THE FUTURE
      }
  
      const actionSheet = await this.actionSheetController.create({
        cssClass: 'light-font-sheet',
        header: event,
        buttons: [
          {
            text: await this.translate.get('btn_copy').toPromise(),
            icon: 'copy',
            role: 'selected',
            cssClass: 'light-font-sheet',
            handler: () => this.doCopy(event),
          },
          {
            text: await this.translate.get('link_labels.link_to_block_explorer').toPromise(),
            icon: 'open',
            role: 'selected',
            cssClass: 'light-font-sheet',
            handler: () => openLinkExternal(`${explorerUrl}/${isAddress ? 'address/' : 'tx/'}${event}`, this.inAppBrowser),
          },
          {
            text: await this.translate.get('btn_cancel').toPromise(),
            icon: 'close',
            role: 'cancel',
            cssClass: 'light-font-sheet',
            handler: () => Logger.info('Cancelled'),
          },
        ],
      });
  
      await actionSheet.present();

  }
  async doCopy(item) {
    handleCopy(item, this.platform, this.clipboard, this.alertController, this.translate, this.clipboardService, null);
  }
}
